/* eslint-disable @angular-eslint/component-class-suffix */
import { ApplicationStatusDescription, ApplicationStatusDescriptionShort, BankAccountStatus, BankAccountStatusDescriptions, BankAccountStatusDescriptionsShort, BankAccountType, CustomerApplicationProduct, IdentificationType, PayCycle } from 'common';
import { BankAccountTypesOptions, IdTypeOptions, MonthlyPayDateOptions, StaticBankList, WeeklyPayDateOptions } from '../../../utils/constants';
import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { DatePipe, DecimalPipe, JsonPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

@Component({
    standalone: true,
    imports: [MatDialogModule, MatDividerModule, MatButtonModule, JsonPipe, DecimalPipe, DatePipe],
    selector: 'lib-customer-product-dialog',
    templateUrl: './customer-product-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
    // Full screen dialog.
    mat-dialog-content {
        max-height: unset !important;
        flex: 1 0 0;
    }

    h4 {
        margin-bottom: 0.25rem;
        margin-top: 0.25rem;
    }

    .reduced-margin {
        margin-top: 0.5rem;

        table {
            display: block;
            table-layout: fixed;

            tr {
                line-height: 1.1rem;
                display: flex;
                
                th {
                    text-align: start;
                    padding-right: 1rem;
                    min-width: 12rem;
                    display: flex;
                }
                td {
                    flex: 1 auto;
                    margin-left: 0.25rem;
                    display: flex;
                }
            }
        }
    }

    .bordered {
        border-left: 1px solid #ccc;
        padding: 0.5rem;
    }

    // Wrap the table rows and columns beneath each other.
    @media screen and (max-width: 600px) {
        table {
            tr {
                border: 1px inset #ccc;
                padding: 0.25rem;
                margin-bottom: 0.25rem;
                line-height: 1.5rem !important;
                flex-wrap: wrap;

                th {
                    flex-basis: 100%;
                    min-width: unset;
                }
                td {
                    flex-grow: unset;
                    flex-shrink: unset;
                    flex-basis: 100%;
                    margin-left: 0 !important;
                }
            }
        }
     }

    `]
})
export class CustomerProductDialog implements OnInit {

    public appli: CustomerApplicationProduct = this.data.application;
    public displayModel = signal<any>({});

    constructor(public dialogRef: MatDialogRef<CustomerProductDialog>,
        @Inject(MAT_DIALOG_DATA) public data: { application: CustomerApplicationProduct }
    ) { }

    public ngOnInit(): void {
        this.updateModel();
    }

    /**
     * A model set with all the values required to read in the html.
     */
    private updateModel() {
        const displayModel = {
            application: {
                status: this.applicationStatus,
                statusDescription: this.applicationStatusDescription,
                baseAmount: this.appli.baseAmount,
                instalment: this.appli.baseInstalment,
                noInstallments: this.appli.instalments,
                startDate: this.applicationDate,
            },
            customer: {
                initials: this.appli.customer.customerInfo.initials,
                idType: this.customerIdType,
                idTitle: this.customerIdTypeTitle,
                idValue: this.appli.customer.customerInfo.customerIdentity,
                fullName: this.appli.customer.customerInfo.customerName,
                email: this.appli.customer.customerInfo.customerEmail,
                mobileNo: this.appli.customer.customerInfo.customerMobile,
                address: this.appli.customer.customerInfo.customerAddress
            },
            employment: {
                employer: this.appli.customerProductEmployment.employerName,
                employeeNo: this.appli.customerProductEmployment.employerNumber,
                branchName: this.appli.customerProductEmployment.branchName,
                address: this.appli.customerProductEmployment.branchAddress,
                employmentStatus: this.data.application.customerProductEmployment.employmentStatus,
                jobTitle: this.appli.customerProductEmployment.designation,
                startDate: this.employmentStartDate,
                payDay: this.payDay,
                payFrequency: this.appli.customerProductEmployment.payCycle,
            },
            bank: {
                name: this.appli.customerProductBankAccount.bank,
                status: this.bankAccountStatus,
                statusLong: this.bankAccountStatusDescription,
                branchCode: this.branchCode || this.appli.customerProductBankAccount.branchCode,
                accountType: this.accountType,
                accountNo: this.appli.customerProductBankAccount.accountNumber,
            }
        };

        this.displayModel.set(displayModel);
    }

    public toInitials(customerName: string) {
        return (customerName || '').split(' ').map(n => n[0]).join('.').toUpperCase();
    }

    public get applicationStatus() {
        return ApplicationStatusDescriptionShort[this.data.application.productStatusId];
    }

    public get applicationStatusDescription() {
        return ApplicationStatusDescription[this.data.application.productStatusId];
    }

    public get applicationDate() {
        return new Date(this.data.application.startDate);
    }

    public get branchCode() {
        const matchingBank = StaticBankList.find(b => b.id === this.data.application.customerProductBankAccount.bankId);
        return matchingBank?.code || '';
    }

    public get bankAccountStatus() {
        const statusId: BankAccountStatus = this.data.application.customerProductBankAccount.bankAccountStatus || 0;
        return BankAccountStatusDescriptionsShort[statusId];
    }

    public get bankAccountStatusDescription() {
        const statusId: BankAccountStatus = this.data.application.customerProductBankAccount.bankAccountStatus || 0;
        return BankAccountStatusDescriptions[statusId];
    }

    public get employmentStartDate() {
        return new Date(this.data.application.customerProductEmployment.dateStart);
    }

    public get payDay() {
        let payCycle = this.data.application.customerProductEmployment.paymentFrequencyId;
        let payDay = this.data.application.customerProductEmployment.payday;

        // Old APIs return the payCycle as a string.
        if (<any>payCycle === 'Monthly') {
            payCycle = PayCycle.Monthly;
        } else if (<any>payCycle === 'Weekly') {
            payCycle = PayCycle.Weekly;
        } else if (<any>payCycle === 'Fortnightly') {
            payCycle = PayCycle.Fortnightly;
        }

        // Old APIs have difference cases.
        if (<any>payDay === undefined) {
            payDay = (this.data.application.customerProductEmployment as any).payDay;
        }

        if (payCycle === PayCycle.Weekly || payCycle === PayCycle.Fortnightly) {
            return WeeklyPayDateOptions.find(d => d.value === payDay)?.display || '';
        } else {
            const match = MonthlyPayDateOptions.find(d => d.value === payDay)?.display || '';
            if (payDay !== -1) {
                return this.toNth(payDay) + '';
            }

            return match;
        }
    }

    private toNth(n: number) {
        const s = ['th', 'st', 'nd', 'rd'],
            v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    public get accountType() {
        return this.toAccountType(this.data.application.customerProductBankAccount.bankAccountType);
    }

    private toAccountType(type: BankAccountType) {
        const matchingAccountType = BankAccountTypesOptions.find(b => b.value === type);
        return matchingAccountType?.display || '';
    }

    public get customerIdType() {
        return this.toIdType(this.data.application.customer.customerInfo.identityType);
    }

    private toIdType(identityType: IdentificationType) {
        const matchingId = IdTypeOptions.find(i => i.value === identityType);
        return matchingId?.display || '';
    }

    public get customerIdTypeTitle() {
        return this.data.application.customer.customerInfo.identityType === 1 ? 'ID No.'
            : this.data.application.customer.customerInfo.identityType === 2 ? 'Passport No.' : 'Given Identity';
    }


}